import dayjs from 'dayjs';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  getSearchListForSearchTermReport,
  getUserList,
  getDocumentAuditActionTypes,
  getReviewLeftPanel,
  getDocumentAuditSearchList,
  getReviewResultsSources,
  getUserActivityActionTypes,
  getUserActivityView,
} from '../../../../api/endpoints/reportingApi';
import {
  AccordionSection,
  FilterComponent,
  Section,
} from '../config/reportTypes';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import utc from 'dayjs/plugin/utc';

export const getUserActivityData = async () => {
  try {
    const [actionTypes, userDetailsResponse] = await Promise.all([
      getUserActivityActionTypes(),
      getUserActivityView(),
    ]);

    return {
      actionTypes: Array.isArray(actionTypes) ? actionTypes : [],
      userDetails: Array.isArray(userDetailsResponse?.userDetails)
        ? userDetailsResponse.userDetails
        : [],
    };
  } catch (error) {
    console.error('Error fetching UserActivityReport data:', error);
    throw error;
  }
};

export const getDocumentAuditData = async () => {
  try {
    const [actionTypes, searchList] = await Promise.all([
      getDocumentAuditActionTypes(),
      getDocumentAuditSearchList(),
    ]);

    return {
      actionTypes: Array.isArray(actionTypes) ? actionTypes : [],
      searchList: Array.isArray(searchList) ? searchList : [],
    };
  } catch (error) {
    console.error('Error fetching DocumentAuditReport data:', error);
    throw error;
  }
};

export const reportApiMap: Record<string, (payload?: any) => Promise<any>> = {
  UserLoginActivityReport: getUserList,
  DocumentAudit: getDocumentAuditData,
  ReviewersCountReport: getReviewLeftPanel,
  SearchTermReport: getSearchListForSearchTermReport,
  ReviewResults: getReviewResultsSources,
  UserActivity: getUserActivityData,
};

export const mergeApiDataIntoConfig = (
  config: any,
  apiData: any,
  initialValuesFromLeftPanel?: any
): any => {
  dayjs.extend(utc);

  const defaultInitialValues = extractInitialValuesFromJson(config);

  const normalizeInitialValues = (raw: any): Record<string, any> => {
    if (Array.isArray(raw)) {
      return {searchesTree: raw};
    }
    return raw || {};
  };

  const mergedInitialValues = {
    ...defaultInitialValues,
    ...normalizeInitialValues(initialValuesFromLeftPanel),
  };

  return {
    ...config,
    selectionCriteria: config.selectionCriteria
      ? {
          ...config.selectionCriteria,
          fields: (config.selectionCriteria.fields || [])
            .filter(
              (field: any) =>
                field.key !== 'sourceCode' && field.id !== 'sourceCode'
            )
            .map((field: any) => {
              return {...field, value: initialValuesFromLeftPanel?.[field.key]};
            }),
        }
      : config.selectionCriteria,
    sections: (config.sections || []).map((section: any) => ({
      ...section,
      filters: section.filters.map((filter: any) => ({
        ...filter,
        components: filter.components.map((comp: any) => {
          if (comp.input_type === 'datePicker') {
            if (comp.key === 'fromDate') {
              let initialValue =
                initialValuesFromLeftPanel?.fromDate ??
                mergedInitialValues?.fromDate ??
                '';

              if (
                typeof initialValue === 'string' &&
                initialValue.includes('T')
              ) {
                initialValue = dayjs
                  .utc(initialValue)
                  .format('YYYY/MM/DD HH:mm:ss');
              }

              if (
                typeof initialValue === 'string' &&
                initialValue.includes('-')
              ) {
                initialValue = dayjs
                  .utc(initialValue)
                  .format('YYYY/MM/DD HH:mm:ss');
              }

              return {
                ...comp,
                maxDateTime: dayjs.utc(),
                initialStartDate: initialValue,
                initialValues: initialValue,
              };
            }

            if (comp.key === 'toDate') {
              let initialValue =
                initialValuesFromLeftPanel?.toDate ??
                mergedInitialValues?.toDate ??
                '';

              if (
                typeof initialValue === 'string' &&
                initialValue.includes('T')
              ) {
                initialValue = dayjs
                  .utc(initialValue)
                  .format('YYYY/MM/DD HH:mm:ss');
              }

              if (
                typeof initialValue === 'string' &&
                initialValue.includes('-')
              ) {
                initialValue = dayjs
                  .utc(initialValue)
                  .format('YYYY/MM/DD HH:mm:ss');
              }

              return {
                ...comp,
                maxDateTime: dayjs.utc(),
                initialEndDate: initialValue,
                initialValues: initialValue,
              };
            }
          }

          if (comp.input_type === 'checkboxList' && filter.id === 'usersList') {
            const transformedData =
              apiData?.userDetails?.map((user: any, index: number) => ({
                label: user.userName,
                value: initialValuesFromLeftPanel?.usersList?.[index] ?? false,
                id: user.userId,
              })) ?? [];
            return {
              ...comp,
              childLabels: transformedData,
              initialValues: mergedInitialValues[comp.key] ?? {},
            };
          }

          if (
            comp.input_type === 'checkboxList' &&
            filter.id === 'actionTypes'
          ) {
            const transformedData = apiData.actionTypes.map(
              (action: any, index: number) => ({
                label: action.actionTypeName,
                value:
                  initialValuesFromLeftPanel?.actionTypes?.[index] ??
                  action.value ??
                  true,
                id: action.actionTypeID,
              })
            );
            return {...comp, childLabels: transformedData};
          }
          if (
            comp.input_type === 'multiSelectTreeViewList' &&
            filter.id === 'searches'
          ) {
            const transformedTreeData = transformDataToTree(apiData);
            return {
              ...comp,
              treeData: transformedTreeData,
              initialValues: mergedInitialValues[comp.key] ?? [],
            };
          }

          if (
            comp.input_type === 'multiSelectTreeViewList' &&
            filter.id === 'searchList'
          ) {
            const transformedTreeData = Array.isArray(apiData.searchList)
              ? transformDataToTree(apiData.searchList)
              : [];
            return {
              ...comp,
              treeData: transformedTreeData,
              initialValues: mergedInitialValues[comp.key] ?? [],
            };
          }
          return {
            ...comp,
            initialValues: mergedInitialValues[comp.key] ?? [],
          };
        }),
      })),
    })),
  };
};

export const resolveSelectedDailyHourly = (
  initialValues?: any
): '1' | '0' => {
  if (!initialValues || Array.isArray(initialValues)) {
    return '1';
  }

  const selected = initialValues.selectedDailyHourly;
  if (selected === '1' || selected === 1) return '1';
  if (selected === '0' || selected === 0) return '0';

  const reportId = Number(initialValues.reportID ?? 0);
  if (!Number.isFinite(reportId) || reportId <= 0) return '1';

  const showDaily = initialValues.showDailyReport;
  if (showDaily === true || showDaily === 1 || showDaily === '1') return '1';
  if (showDaily === false || showDaily === 0 || showDaily === '0') return '0';

  return '1';
};

export const mergeApiDataIntoConfigForReviewersCount = (
  config: any,
  apiData: any,
  initialValuesFromLeftPanel?: any,
  isDailySwitched?: boolean
): any => {
  dayjs.extend(utc);

  const defaultInitialValues = extractInitialValuesFromJson(config);

  const normalizeInitialValues = (raw: any): Record<string, any> => {
    if (Array.isArray(raw)) {
      return {searchesTree: raw};
    }
    return raw || {};
  };

  const mergedInitialValues = {
    ...defaultInitialValues,
    ...normalizeInitialValues(initialValuesFromLeftPanel),
  };

  const selectedDailyHourlyValue = resolveSelectedDailyHourly(
    initialValuesFromLeftPanel
  );
  const returnValues = {
    ...config,

    selectionCriteria: config.selectionCriteria
      ? {
          ...config.selectionCriteria,
          fields: (config.selectionCriteria.fields || [])
            .filter(
              (field: any) =>
                field.key !== 'sourceCode' && field.id !== 'sourceCode'
            )
            .map((field: any) => {
              if (field.key === 'selectedDailyHourly') {
                return {
                  ...field,
                  value: selectedDailyHourlyValue,
                };
              }
              return {...field, value: initialValuesFromLeftPanel?.[field.key]};
            }),
        }
      : config.selectionCriteria,
    sections: (config.sections || []).map((section: any) => ({
      ...section,
      filters: section.filters.map((filter: any) => ({
        ...filter,
        components: filter.components.map((comp: any) => {
          if (comp.input_type === 'checkboxList' && filter.id === 'reviewers') {
            const sel = mergedInitialValues?.selectedReviewers;
            const byIds =
              Array.isArray(sel) && sel.length && typeof sel[0] !== 'boolean'
                ? new Set((sel as any[]).map((x) => String(x)))
                : null;
            const byLabel =
              sel && typeof sel === 'object' && !Array.isArray(sel)
                ? (sel as Record<string, boolean>)
                : null;
            const byIndex =
              Array.isArray(sel) &&
              sel.length === (apiData?.reviewers?.length ?? 0) &&
              typeof sel[0] === 'boolean'
                ? (sel as boolean[])
                : null;
            const transformedData =
              apiData?.reviewers?.map((user: any, idx: number) => {
                const vFromIndex = byIndex ? Boolean(byIndex[idx]) : undefined;
                const vFromLabel = byLabel
                  ? Boolean(byLabel[user.reviewer_name])
                  : undefined;
                const vFromIds = byIds
                  ? byIds.has(String(user.actionTypeID))
                  : undefined;
                const value =
                  vFromIndex !== undefined
                    ? vFromIndex
                    : vFromLabel !== undefined
                      ? vFromLabel
                      : vFromIds !== undefined
                        ? vFromIds
                        : false;
                return {
                  label: user.reviewer_name,
                  id: String(user.actionTypeID),
                  value,
                };
              }) ?? [];

            return {
              ...comp,
              childLabels: transformedData,
              initialValues: mergedInitialValues[comp.key] ?? {},
            };
          }

          if (
            comp.input_type === 'multiSelectTreeViewList' &&
            filter.id === 'assignmentGroups'
          ) {
            const buildTreeItems = transformDataToTree;

            const transformedData = Array.isArray(apiData.assignmentGroups)
              ? buildTreeItems(apiData.assignmentGroups)
              : [];
            const mapSelectedAssignments = (id: number) => id?.toString();
            return {
              ...comp,
              treeData: transformedData,
              initialValues: (
                mergedInitialValues.selectedAssignments ?? []
              ).map(mapSelectedAssignments),
            };
          }

          if (
            comp.input_type === 'datePicker' &&
            filter.id === 'dateRangeSection'
          ) {
            const isDaily = selectedDailyHourlyValue === '1';

            const now = dayjs.utc();

            const defaultFrom = isDaily
              ? now.subtract(7, 'day').format('YYYY/MM/DD')
              : now.subtract(7, 'day').format('YYYY/MM/DD HH:mm:ss');

            const defaultTo = isDaily
              ? now.format('YYYY/MM/DD')
              : now.format('YYYY/MM/DD HH:mm:ss');

            let fromValue =
              initialValuesFromLeftPanel?.fromDateTime ??
              mergedInitialValues?.fromDateTime ??
              defaultFrom;
            let toValue =
              initialValuesFromLeftPanel?.toDateTime ??
              mergedInitialValues?.toDateTime ??
              defaultTo;

            if (
              isDailySwitched &&
              initialValuesFromLeftPanel?.showDailyReport
            ) {
              fromValue = dayjs.utc();
              toValue = dayjs.utc();
            }

            const dateFormat = isDaily ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm:ss';

            if (fromValue) fromValue = dayjs.utc(fromValue).format(dateFormat);
            if (toValue) toValue = dayjs.utc(toValue).format(dateFormat);

            if (comp.key === 'fromDate') {
              return {
                ...comp,
                maxDateTime: dayjs.utc(),
                initialStartDate: fromValue,
                initialValues: fromValue,
                placeHolder: dateFormat,
              };
            }
            if (comp.key === 'toDate') {
              return {
                ...comp,
                maxDateTime: dayjs.utc(),
                initialStartDate: toValue,
                initialValues: toValue,
                placeHolder: dateFormat,
              };
            }
            return {
              ...comp,
              maxDateTime: dayjs.utc(),
            };
          }

          return {
            ...comp,
            initialValues: mergedInitialValues[comp.key] ?? [],
          };
        }),
      })),
    })),
  };
  return returnValues;
};

export const mapSelectedUsers = (
  selectedKeys: boolean[],
  userDetails: any[]
): any[] => {
  const checkedFields: any[] = [];
  if (Array.isArray(selectedKeys) && Array.isArray(userDetails)) {
    for (let i = 0; i < selectedKeys.length; i++) {
      if (selectedKeys[i] === true) {
        checkedFields.push(userDetails[i]);
      }
    }
  }
  return checkedFields;
};

export const mergeFormatDataIntoConfig = (config: any, apiData: any) => {
  return {
    ...config,
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (filter.id === 'exportData') {
              if (comp.key === 'style') {
                const styleIndex =
                  apiData?.exportModelData?.styleList.findIndex(
                    (style: any) =>
                      style.label === apiData?.selectedSourceDetails?.style
                  );
                return {
                  ...comp,
                  dropdownItem: apiData?.exportModelData?.styleList,
                  selectedValue:
                    styleIndex !== -1
                      ? apiData?.exportModelData?.styleList[styleIndex]
                      : apiData?.exportModelData?.styleList[0],
                };
              }

              if (comp.key === 'field') {
                const fieldIndex =
                  apiData?.exportModelData?.fieldList.findIndex(
                    (field: any) =>
                      field.label === apiData?.selectedSourceDetails?.field
                  );
                return {
                  ...comp,
                  dropdownItem: apiData?.exportModelData?.fieldList,
                  selectedValue:
                    fieldIndex !== -1
                      ? apiData?.exportModelData?.fieldList[fieldIndex]
                      : apiData?.exportModelData?.fieldList[44],
                };
              }

              if (comp.key === 'text') {
                const textIndex = apiData?.exportModelData?.textList.findIndex(
                  (text: any) =>
                    text.label === apiData?.selectedSourceDetails?.text
                );
                return {
                  ...comp,
                  dropdownItem: apiData?.exportModelData?.textList,
                  selectedValue:
                    textIndex !== -1
                      ? apiData?.exportModelData?.textList[textIndex]
                      : apiData?.exportModelData?.textList[34],
                };
              }

              if (comp.key === 'newLine') {
                const newLineIndex =
                  apiData?.exportModelData?.newLineList.findIndex(
                    (newLine: any) =>
                      newLine.label === apiData?.selectedSourceDetails?.newLine
                  );
                return {
                  ...comp,
                  dropdownItem: apiData?.exportModelData?.newLineList,
                  selectedValue:
                    newLineIndex !== -1
                      ? apiData?.exportModelData?.newLineList[newLineIndex]
                      : apiData?.exportModelData?.newLineList[59],
                };
              }

              if (comp.key === 'dateStyle') {
                const dateStyleIndex =
                  apiData?.exportModelData?.dateStyleList.findIndex(
                    (dateStyle: any) =>
                      dateStyle.label ===
                      apiData?.selectedSourceDetails?.dateStyle
                  );
                return {
                  ...comp,
                  dropdownItem: apiData?.exportModelData?.dateStyleList,
                  selectedValue:
                    dateStyleIndex !== -1
                      ? apiData?.exportModelData?.dateStyleList[dateStyleIndex]
                      : apiData?.exportModelData?.dateStyleList[0],
                };
              }
            }

            return comp;
          }),
        };
      }),
    })),
  };
};

interface SourceItem {
  id: string;
  text: string;
  parent: string;
  isGroup: boolean;
  searchGroupTypeCode: string;
  documentCount: number;
}

export const transformDataToTree = (
  data: SourceItem[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  if (!Array.isArray(data)) return [];
  const map = new Map<string, ExtendedTreeItemProps>();
  data?.forEach((item, index) => {
    const nodeId = item.id && item.id.trim() !== '' ? item.id : `node-${index}`;
    const node: ExtendedTreeItemProps = {
      id: nodeId,
      label: item.text || `Unnamed-${index}`,
      name: (item.text || '').toLowerCase().replace(/\s+/g, '-'),
      fileType: item.isGroup ? 'folder' : 'file',
      value: nodeId,
    };
    map.set(nodeId, node);
  });

  const tree: ExtendedTreeItemProps[] = [];
  data?.forEach((item, idx) => {
    const nodeId = item.id || `node-${idx}`;
    if (item.parent === '#') {
      const rootNode = map.get(nodeId);
      if (rootNode) tree.push(rootNode);
    } else {
      const parentNode = map.get(item.parent);
      const childNode = map.get(nodeId);
      if (parentNode && childNode) {
        if (!parentNode.children) parentNode.children = [];
        parentNode.children.push(childNode);
      }
    }
  });

  // Disable childless parent nodes
  tree.forEach((node) => {
    const nodeIsParent = node.fileType === 'folder';
    const nodeHasChildren = (node.children?.length ?? 0) > 0;
    if (nodeIsParent && !nodeHasChildren) {
      node.disabled = true;
    }
  });

  return tree;
};

export const extractInitialValuesFromJson = (
  config: any
): Record<string, any> => {
  const initialValues: Record<string, any> = {};
  if (config?.selectionCriteria?.fields) {
    config.selectionCriteria.fields.forEach((field: any) => {
      if (field.key) {
        initialValues[field.key] = field.value ?? [];
      }
    });
  }
  if (config?.sections) {
    config.sections.forEach((section: any) => {
      section.filters.forEach((filter: any) => {
        filter.components.forEach((component: any) => {
          if (component.key) {
            if (component.value !== undefined) {
              initialValues[component.key] = component.value;
            } else if (
              component.input_type === 'checkboxList' &&
              Array.isArray(component.childLabels)
            ) {
              initialValues[component.key] = component.childLabels.reduce(
                (acc: Record<string, boolean>, item: any) => {
                  acc[item.label] = item.value;
                  return acc;
                },
                {}
              );
            } else {
              initialValues[component.key] = [];
            }
          }
        });
      });
    });
  }
  return initialValues;
};

const extractInitialValuesFromJsons = (config: any) =>
  config?.initialValues || {};

const toDateOnlyString = (v: any) => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) return s;
  if (/^\d{4}\/\d{2}\/\d{2}\s+/.test(s)) return s.slice(0, 10);
  return s;
};

const normalizeInitialValues = (raw: any) => {
  if (Array.isArray(raw)) return {searchesTree: raw};

  const base = raw ?? {};

  const toStringArray = (v: any) =>
    (Array.isArray(v) ? v : v ? [v] : [])
      .map((x) => String(x))
      .filter((x) => x !== '');

  const from = toDateOnlyString(base.fromDate ?? base.fromDateTime);
  const to = toDateOnlyString(base.toDate ?? base.toDateTime);

  return {
    ...base,
    fromDate: from,
    toDate: to,
    tagsTree: toStringArray(base.tagsTree ?? base.selectedTags),
    redactionTree: toStringArray(
      base.redactionTree ?? base.selectedRedactionTags
    ),
    productionTree: toStringArray(
      base.productionTree ?? base.selectedProductions
    ),
  };
};

const transformSelectedSourcesDropdown = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => {
  const options = (apiData.searchesList || []).map((x: any) => ({
    label: x.text,
    value: x.id,
  }));
  return {
    ...comp,
    options,
    initialValues: mergedInitialValues[comp.key] ?? '',
  };
};

const transformDocumentTypes = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => {
  const selectedIds = Array.isArray(mergedInitialValues?.selectedDocumentTypes)
    ? mergedInitialValues.selectedDocumentTypes
    : [];
  const selectedSet = new Set(selectedIds.map((x: any) => String(x)));

  const labels = (apiData.documentTypesList ?? []).map((d: any) => ({
    label: d.kayName,
    id: d.keyID,
    value: selectedSet.has(String(d.keyID)),
  }));

  return {
    ...comp,
    childLabels: labels,
    initialValues: mergedInitialValues[comp.key] ?? {},
  };
};

const transformTagActionTypes = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => {
  const selectedIds = Array.isArray(mergedInitialValues?.selected_ActionTypeID)
    ? mergedInitialValues.selected_ActionTypeID
    : [];
  const selectedSet = new Set(selectedIds.map((x: any) => String(x)));

  const labels = (apiData.actionTypesList ?? []).map((a: any) => ({
    label: a.actionTypeName,
    id: a.actionTypeID,
    value: selectedSet.has(String(a.actionTypeID)),
  }));

  return {
    ...comp,
    childLabels: labels,
    initialValues: mergedInitialValues[comp.key] ?? {},
  };
};

const transformTagsTree = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => ({
  ...comp,
  treeData: transformDataToTree(apiData.tagsList),
  initialValues: (mergedInitialValues[comp.key] ?? []).map((x: any) =>
    String(x)
  ),
});

const transformTagClassificationCheckbox = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => {
  const selectedIds = Array.isArray(
    mergedInitialValues?.selectedTagClassification
  )
    ? mergedInitialValues.selectedTagClassification
    : [];
  const selectedSet = new Set(selectedIds.map((x: any) => String(x)));

  return {
    ...comp,
    childLabels: (apiData.tagClassifications ?? []).map((c: any) => ({
      label: c.kayName,
      id: String(c.keyID),
      value: selectedSet.has(String(c.keyID)),
    })),
    initialValues: mergedInitialValues[comp.key] ?? {},
  };
};

const transformRedactionTree = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => ({
  ...comp,
  treeData: transformDataToTree(apiData.redactionTagsList),
  initialValues: (mergedInitialValues[comp.key] ?? []).map((x: any) =>
    String(x)
  ),
});

const transformCommentTypes = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => {
  const selectedIds = Array.isArray(mergedInitialValues?.selectedCommentTypes)
    ? mergedInitialValues.selectedCommentTypes
    : [];
  const selectedSet = new Set(selectedIds.map((x: any) => String(x)));

  const labels = (apiData.commentsTypeList ?? []).map((d: any) => ({
    label: d.kayName,
    id: d.keyID,
    value: selectedSet.has(String(d.keyID)),
  }));

  return {
    ...comp,
    childLabels: labels,
    initialValues: mergedInitialValues[comp.key] ?? {},
  };
};

const transformProductionTree = (
  apiData: any,
  comp: any,
  mergedInitialValues: any
) => ({
  ...comp,
  treeData: transformDataToTree(apiData.productionsList),
  initialValues: (mergedInitialValues[comp.key] ?? []).map((x: any) =>
    String(x)
  ),
});

const transformComponent = (
  filter: any,
  comp: any,
  apiData: any,
  mergedInitialValues: any
) => {
  if (comp.input_type === 'datePicker') {
    const direct = mergedInitialValues?.[comp.key];
    const fallback =
      comp.key === 'fromDate'
        ? mergedInitialValues?.fromDateTime
        : comp.key === 'toDate'
          ? mergedInitialValues?.toDateTime
          : undefined;
    const raw = direct ?? fallback ?? comp.value ?? '';
    const isDateTime = comp.mode === 'date-time';
    const val = isDateTime ? String(raw ?? '').trim() : toDateOnlyString(raw);
    return {
      ...comp,
      value: val,
      initialValues: val,
      ...(comp.key === 'fromDate' ? {initialStartDate: val} : {}),
      ...(comp.key === 'toDate' ? {initialEndDate: val} : {}),
      dateFormat:
        (comp as any).dateFormat ??
        (isDateTime ? 'YYYY/MM/DD HH:mm:ss' : 'YYYY/MM/DD'),
    };
  }

  if (comp.key === 'selectedSources' && comp.input_type === 'dropdown') {
    return transformSelectedSourcesDropdown(apiData, comp, mergedInitialValues);
  }

  if (filter.id === 'documentTypeSection' && comp.key === 'documentTypes') {
    return transformDocumentTypes(apiData, comp, mergedInitialValues);
  }

  if (filter.id === 'TagActionTypesSection' && comp.key === 'TagActionTypes') {
    return transformTagActionTypes(apiData, comp, mergedInitialValues);
  }

  if (filter.id === 'tags' && comp.key === 'tagClassification') {
    return transformTagClassificationCheckbox(
      apiData,
      comp,
      mergedInitialValues
    );
  }

  if (filter.id === 'tags' && comp.key === 'tagsTree') {
    return transformTagsTree(apiData, comp, mergedInitialValues);
  }

  if (filter.id === 'reductionTagsSection' && comp.key === 'redactionTree') {
    return transformRedactionTree(apiData, comp, mergedInitialValues);
  }

  if (
    filter.id === 'CommentTypesSection_Output' &&
    comp.key === 'commentTypes'
  ) {
    const data = transformCommentTypes(apiData, comp, mergedInitialValues);

    const updatedData = {
      ...data,
      childLabels: data?.childLabels?.filter(
        (item: any) => item?.label?.toLowerCase() !== 'all comments'
      ),
    };

    return updatedData;
  }

  if (filter.id === 'production' && comp.key === 'productionTree') {
    return transformProductionTree(apiData, comp, mergedInitialValues);
  }

  return {...comp, initialValues: mergedInitialValues[comp.key] ?? []};
};

const getTagIdsByClassification = (
  tagsList: any[],
  selectedClassifications: string[]
): string[] => {
  return tagsList
    .filter((tag) =>
      selectedClassifications.includes(String(tag.classificationID))
    )
    .map((tag) => String(tag.id));
};

export const syncClassificationToTagsTree = (
  selectedClassifications: string[],
  currentTags: string[],
  apiData: any
): string[] => {
  if (!selectedClassifications?.length) return currentTags;
  const classifiedTags = getTagIdsByClassification(
    apiData.tagsList,
    selectedClassifications
  );
  // merge & dedupe
  return Array.from(new Set([...currentTags, ...classifiedTags]));
};

export const mergeApiDataIntoConfigForReviewResult = (
  config: any,
  apiData: any,
  initialValuesFromLeftPanel?: any
) => {
  const defaultInitialValues = extractInitialValuesFromJsons(config);
  const mergedInitialValues = {
    ...defaultInitialValues,
    ...normalizeInitialValues(initialValuesFromLeftPanel),
  };

  return {
    ...config,
    selectionCriteria: {
      ...config.selectionCriteria,
      fields: config.selectionCriteria.fields.map((field: any) => ({
        ...field,
        value: mergedInitialValues[field.key] ?? '',
      })),
    },
    sections: config.sections.map((section: any) => ({
      ...section,
      filters: section.filters.map((filter: any) => ({
        ...filter,
        components: filter.components.map((comp: any) =>
          transformComponent(filter, comp, apiData, mergedInitialValues)
        ),
      })),
    })),
  };
};

export const chunkRows = (flatArray: string[], columns: number): string[][] => {
  const rows: string[][] = [];
  for (let i = 0; i < flatArray?.length; i += columns) {
    rows.push(flatArray.slice(i, i + columns));
  }
  return rows;
};

export const mapRowsWithHeaders = (
  headers: string[] = [],
  rowData: string[] | string[][] = []
) => {
  if (!headers.length || !rowData) return [];
  const rows: string[][] = Array.isArray((rowData as any)[0])
    ? (rowData as string[][])
    : [rowData as string[]];
  return rows.map((rowArray, rowIndex) => {
    const row: Record<string, any> = {};
    headers.forEach((header, index) => {
      row[header] = (rowArray as any)[index] ?? '';
    });
    return {
      id: (row as any).SourceID ?? String(rowIndex),
      ...row,
    };
  });
};

export const createTableColumns = (headers: string[], tableHaeder = '') => {
  if (!headers?.length) return [];
  let isSourcePath: boolean;
  const sourcePath: string = 'SourcePath';
  return headers.map((header, index) => {
    const isPrimaryColumn = index <= 3;
    if (tableHaeder == sourcePath && header == sourcePath) {
      isSourcePath = true;
    } else {
      isSourcePath = false;
    }
    return {
      field: header,
      headerName: header,
      flex: isPrimaryColumn ? 1 : undefined,
      minWidth: isSourcePath ? 380 : isPrimaryColumn ? 220 : 250,
      isVisible: true,
      index,
      sortable: true,
    };
  });
};

export const resolveSelectedSource = (ui: any) => {
  if (ui.SECURITYGROUPS?.length) {
    return {
      selectedSource: 'SECURITYGROUPS',
      selectedSourceID: ui.SECURITYGROUPS.flatMap((x: any) => x.source),
    };
  }
  if (ui.CUSTODIANS?.length) {
    return {
      selectedSource: 'CUSTODIANS',
      selectedSourceID: ui.CUSTODIANS.flatMap((x: any) => x.source),
    };
  }
  if (ui.REVIEWERS?.length) {
    return {
      selectedSource: 'REVIEWERS',
      selectedSourceID: ui.REVIEWERS.flatMap((x: any) => x.source),
    };
  }
  if (ui.SEARCHES?.length) {
    return {
      selectedSource: 'SEARCHES',
      selectedSourceID: ui.SEARCHES.flatMap((x: any) => x.source),
    };
  }
  if (ui.ASSIGNMENTS?.length) {
    return {
      selectedSource: 'ASSIGNMENTS',
      selectedSourceID: ui.ASSIGNMENTS.flatMap((x: any) => x.source),
    };
  }
  if (ui.FOLDERS?.length) {
    return {
      selectedSource: 'FOLDERS',
      selectedSourceID: ui.FOLDERS.flatMap((x: any) => x.source),
    };
  }
};

export const extractFilterFieldsForReviewResults = (obj: any) => ({
  selected_ActionTypeID: obj.selected_ActionTypeID ?? [],
  selected_SearchID: obj.selected_SearchID ?? [],
  selectedDocumentTypes: obj.selectedDocumentTypes ?? [],
  selectedTagClassification: obj.selectedTagClassification ?? [],
  selectedTags: obj.selectedTags ?? [],
  selectedRedactionTags: obj.selectedRedactionTags ?? [],
  selectedCommentTypes: obj.selectedCommentTypes ?? [],
  selectedProductions: obj.selectedProductions ?? [],
  selectedSourceID: obj.selectedSourceID ?? [],
  selectedSource: obj.selectedSource ?? '',
  fromDateTime: obj.fromDateTime ?? '',
  toDateTime: obj.toDateTime ?? '',
  selectedCustomColumns: obj.selectedCustomColumns ?? [],
});
